import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Spinner, Card, ToggleSwitch } from 'flowbite-react';
import * as wasm from 'sched';
import { useDatabase } from '../../hooks/useDatabase';
import { useDecision } from '../../hooks/useDecision';
import EditableTitle from '../../components/EditableTitle';
import DependencyViewer from '../../components/dependencies/DependencyViewer';
import ListWithActions from '../../components/common/ListWithActions';
import { FiMinusCircle, FiPlus } from 'react-icons/fi';

const DecisionDetailPage: React.FC = () => {
  const { decisionId } = useParams<{ decisionId: string }>();
  const { decision, loading, error } = useDecision(decisionId);
  const { applyCommand } = useDatabase();
  const [newChoice, setNewChoice] = useState('');

  const updateTitle = async (newTitle: string) => {
    if (!decisionId) return;
    const command: wasm.DatabaseCommand = {
      type: 'UpdateDecision',
      id: decisionId,
      actions: [{ type: 'SetTitle', title: newTitle }],
    };
    await applyCommand(command);
  };

  const toggleExclusive = async (val: boolean) => {
    if (!decisionId) return;
    const command: wasm.DatabaseCommand = {
      type: 'UpdateDecision',
      id: decisionId,
      actions: [{ type: 'SetExclusive', exclusive: val }],
    };
    await applyCommand(command);
  };

  const setChoices = async (choices: string[]) => {
    if (!decisionId) return;
    const command: wasm.DatabaseCommand = {
      type: 'UpdateDecision',
      id: decisionId,
      actions: [{ type: 'SetChoices', choices }],
    };
    await applyCommand(command);
  };

  const handleAddChoice = async () => {
    if (!decision || !newChoice.trim()) return;
    const trimmed = newChoice.trim();
    if (!decision.choices.includes(trimmed)) {
      await setChoices([...decision.choices, trimmed]);
    }
    setNewChoice('');
  };

  const handleRemoveChoice = async (choice: string) => {
    if (!decision) return;
    await setChoices(decision.choices.filter(c => c !== choice));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !decision) {
    return (
      <div className="p-4 text-red-600">
        {error ?? 'Decision not found'}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <EditableTitle
        title={decision.title}
        onTitleChange={updateTitle}
      />

      <Card>
        <div className="flex items-center gap-3">
          <ToggleSwitch
            checked={decision.exclusive}
            onChange={toggleExclusive}
            label="Exclusive (only one choice may be selected at a time)"
          />
        </div>
      </Card>

      <Card>
        <h3 className="text-lg font-medium text-cm-text-primary mb-3">Choices</h3>
        {decision.choices.length === 0 && (
          <p className="text-sm text-cm-text-muted mb-2">
            No choices defined. Any dependent is simply gated on this decision.
          </p>
        )}
        <ListWithActions
          items={decision.choices.map(c => ({
            key: c,
            left: <span className="text-sm text-cm-text-primary">{c}</span>,
            right: (
              <button
                className="text-red-500 hover:text-red-600 p-1 rounded hover:bg-cm-bg-hover"
                aria-label="Remove choice"
                onClick={() => handleRemoveChoice(c)}
              >
                <FiMinusCircle className="w-4 h-4" />
              </button>
            ),
          }))}
        />
        <div className="flex gap-2 mt-3">
          <input
            type="text"
            value={newChoice}
            onChange={e => setNewChoice(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddChoice(); } }}
            placeholder="Add a choice..."
            className="flex-1 bg-cm-bg-elevated border border-cm-border-subtle text-cm-text-primary text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 p-2.5"
          />
          <button
            type="button"
            onClick={handleAddChoice}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-blue-700 rounded-lg hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 dark:bg-blue-600 dark:hover:bg-blue-700"
          >
            <FiPlus />
          </button>
        </div>
      </Card>

      {decisionId && (
        <div>
          <h2 className="text-xl font-semibold mb-3">Dependencies & Schedule</h2>
          <Card>
            <DependencyViewer nodeId={{ Decision: decisionId }} height="500px" />
          </Card>
        </div>
      )}
    </div>
  );
};

export default DecisionDetailPage;
